import React, { useCallback, useRef } from 'react';
import DatePicker from "react-datepicker";
import { Spinner } from "react-bootstrap";
import "react-datepicker/dist/react-datepicker.css";
import "./DatePicker.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarXmark,
  faCalendarAlt,
  faTimes
} from "@fortawesome/free-solid-svg-icons";

export default function CustomDatePicker({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onRangeModeChange,
  rangeMode,
  disabled,
  isLoading,
  backtestStatus
}) {
  // Add refs for date pickers to programmatically open them
  const startDatePickerRef = useRef(null);
  const endDatePickerRef = useRef(null);

  // Fixed reset dates function to use props
  const handleClearAll = useCallback(() => {
    onStartDateChange(null);
    onEndDateChange(null);
  }, [onStartDateChange, onEndDateChange]);

  // Custom clear handlers that will open the date picker after clearing
  const handleClearStart = useCallback(() => {
    onStartDateChange(null);
    setTimeout(() => {
      if (startDatePickerRef.current) {
        startDatePickerRef.current.setOpen(true);
      }
    }, 0);
  }, [onStartDateChange]);

  const handleClearEnd = useCallback(() => {
    onEndDateChange(null);
    setTimeout(() => {
      if (endDatePickerRef.current) {
        endDatePickerRef.current.setOpen(true);
      }
    }, 0);
  }, [onEndDateChange]);
  
  if (isLoading) {
    return (
      <div className="period-selection period-selection-loading">
        <div className="backtest-status">
          <div className="backtest-status-title">
            <Spinner size="sm" animation="border" /> 
            <span>Backtest in Progress</span>
          </div>
          {backtestStatus && (
            <div className="backtest-status-details">
              Current stage: <strong>{backtestStatus}</strong>
            </div>
          )}
          <div className="loading-shimmer">
            <div className="shimmer-line" style={{ width: '60%' }}></div>
            <div className="shimmer-line" style={{ width: '80%' }}></div>
            <div className="shimmer-line" style={{ width: '40%' }}></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="period-selection">
      <div className="custom-date-range">
        <div className="date-pickers">
          <div className="date-field">
            <label htmlFor="start-date">Start Date</label>
            <div className="date-picker-wrapper">
              <FontAwesomeIcon icon={faCalendarAlt} className="date-picker-icon" aria-hidden="true" />
              <DatePicker
                id="start-date"
                selected={startDate ? new Date(startDate) : null}
                onChange={(date) => {
                  const formattedDate = date ? date.toISOString().split("T")[0] : null;
                  onStartDateChange(formattedDate);
                }}
                isClearable={false}
                dateFormat="MMM d, yyyy"
                disabled={disabled}
                maxDate={endDate ? new Date(endDate) : new Date()}
                minDate={new Date("1990-01-01")}
                className="date-picker-input"
                placeholderText="Select start date"
                aria-label="Select start date"
                autoComplete="off"
                calendarClassName="dark-theme-calendar"
                ref={startDatePickerRef}
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
              />
              {startDate && (
                <button
                  className="date-picker-clear-btn"
                  onClick={handleClearStart}
                  type="button"
                  aria-label="Clear start date"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>
          </div>

          <div className="date-field">
            <label htmlFor="end-date">End Date</label>
            <div className="date-picker-wrapper">
              <FontAwesomeIcon icon={faCalendarAlt} className="date-picker-icon" aria-hidden="true" />
              <DatePicker
                id="end-date"
                selected={endDate ? new Date(endDate) : null}
                onChange={(date) => {
                  const formattedDate = date ? date.toISOString().split("T")[0] : null;
                  onEndDateChange(formattedDate);
                }}
                isClearable={false}
                dateFormat="MMM d, yyyy"
                disabled={disabled}
                minDate={startDate ? new Date(startDate) : new Date("1990-01-01")}
                maxDate={new Date()}
                className="date-picker-input"
                placeholderText="Select end date"
                aria-label="Select end date"
                autoComplete="off"
                calendarClassName="dark-theme-calendar"
                ref={endDatePickerRef}
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
              />
              {endDate && (
                <button
                  className="date-picker-clear-btn"
                  onClick={handleClearEnd}
                  type="button"
                  aria-label="Clear end date"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="date-guidance-row">
          <p className="date-selection-guidance">
            Select a start and end date to define the backtest period.
          </p>

          <div className="date-controls-container">
            <button
              className="reset-dates-link"
              onClick={handleClearAll}
              disabled={disabled || (!startDate && !endDate)}
              aria-label="Reset date fields"
              type="button"
            >
              <FontAwesomeIcon icon={faCalendarXmark} aria-hidden="true" />
              <span>Reset</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
